// Auth with offline-tolerant cold start.
//
// Old behavior: any failure of GET /api/me on mount cleared the token and
// kicked the user to the login screen — including transient network errors,
// which is unacceptable for a phone that opens the app in airplane mode.
//
// New behavior:
//   1. On mount, if a token exists, hydrate from the IDB-cached user row
//      immediately so the app paints signed-in without waiting on the radio.
//   2. Fire GET /api/me in the background. Distinguish:
//        - 2xx               → refresh cache, stay signed in
//        - 401               → real auth failure (revoked/expired), sign out
//        - anything else     → keep cached user; we'll re-validate next time
//      The key change is that a network error or 5xx no longer signs the
//      user out — fetchWithRetry has already exhausted its retries by then,
//      so a throw means "we couldn't reach the server," not "credentials bad."
//   3. logout() also wipes cached user + outbox + offline data so the device
//      doesn't carry the previous user's state into the next session.

import { useCallback, useEffect, useState } from 'react';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { logger, setUserContext, clearUserContext } from '../utils/logger';
import { clearAllOfflineData, getCachedUser, putCachedUser } from '../offline/cachedUser';

const log = logger.child('useAuth');

export const useAuth = (apiUrl) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Online validate-and-refresh. Returns the server user data on success.
  // Throws on 401 (caller signs out). Returns null on network error (caller
  // keeps current state — could be cached user, could be nothing).
  const fetchUserData = useCallback(async (token) => {
    let res;
    try {
      res = await fetchWithRetry(`${apiUrl}/api/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
    } catch (error) {
      log.warn('me fetch unreachable; keeping current auth state', error);
      return null;
    }

    if (res.status === 401) {
      log.warn('me returned 401; clearing token');
      localStorage.removeItem('token');
      setUser(null);
      clearUserContext();
      try { await clearAllOfflineData(); } catch (e) { log.warn('clearAllOfflineData failed', e); }
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      log.warn('me returned non-ok; keeping current auth state', { status: res.status });
      return null;
    }

    const userData = await res.json();
    setUser({
      token,
      email: userData.email,
      created_at: userData.created_at,
      is_support_agent: !!userData.is_support_agent,
    });
    setUserContext({ userId: userData.id ?? userData.user_id ?? userData.email });
    try { await putCachedUser(userData); } catch (e) { log.warn('putCachedUser failed', e); }
    return userData;
  }, [apiUrl]);

  const logout = useCallback(async () => {
    localStorage.removeItem('token');
    setUser(null);
    clearUserContext();
    try { await clearAllOfflineData(); } catch (e) { log.warn('clearAllOfflineData failed on logout', e); }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoading(false);
      return;
    }
    (async () => {
      // Hydrate from cache first — this is what unblocks offline cold start.
      try {
        const cached = await getCachedUser();
        if (cached && !cancelled) {
          setUser({
            token,
            email: cached.user.email,
            created_at: cached.user.created_at,
            is_support_agent: !!cached.user.is_support_agent,
          });
          setUserContext({ userId: cached.user.id ?? cached.user.user_id ?? cached.user.email });
          setIsLoading(false);
        }
      } catch (e) {
        log.warn('cached user hydrate failed', e);
      }
      // Background validate. On 401 useAuth signs out; on network error we
      // keep the hydrated user. Either way the loading flag should drop.
      try { await fetchUserData(token); }
      catch (_) { /* already handled in fetchUserData */ }
      finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchUserData]);

  return {
    user,
    setUser,
    isLoading,
    fetchUserData,
    logout,
  };
};
